import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  LeadDetails,
  Message,
  FaqSource,
} from "../../pages/Communications/utils/types";
import { PmsSourceData } from "../../components/Sources/utils/types";

interface MessagesState {
  messages: Message[];
  averageAiResponseTime: number;
  averageLeadResponseTime: number;
  leadDetails: LeadDetails | null;
  defaultChannel: string;
  faqSources: FaqSource[];
  pmsSources: PmsSourceData | null;
  sourceMessage: string | null;
  sourceLoading: boolean;
  customQaDrawerOpen: boolean;
}

const initialState: MessagesState = {
  messages: [],
  averageAiResponseTime: 0,
  averageLeadResponseTime: 0,
  leadDetails: null,
  defaultChannel: "",
  faqSources: [],
  pmsSources: null,
  sourceMessage: "No sources available",
  sourceLoading: false,
  customQaDrawerOpen: false,
};

const crmMessageSlice = createSlice({
  name: "crmMessage",
  initialState,
  reducers: {
    setCrmMessages: (
      state,
      action: PayloadAction<{
        messages: Message[];
        averageAiResponseTime: number;
        averageLeadResponseTime: number;
      }>,
    ) => {
      state.messages = action.payload.messages;
      state.averageAiResponseTime = action.payload.averageAiResponseTime;
      state.averageLeadResponseTime = action.payload.averageLeadResponseTime;
    },
    clearCrmMessages: (state) => {
      state.messages = [];
      state.averageAiResponseTime = 0;
      state.averageLeadResponseTime = 0;
    },
    appendCrmMessage: (state, action: PayloadAction<Message>) => {
      const alreadyAppended = state.messages.find(
        (msg) => msg?.id === action?.payload?.id,
      );
      if (!alreadyAppended) {
        state.messages.push(action.payload);
      }
    },
    replaceCrmMessage: (state, action: PayloadAction<Message>) => {
      const message = action.payload as Message;
      const index = state.messages.findIndex((msg) => msg.id === message.id);
      if (index !== -1) {
        state.messages[index] = message;
      } else {
        state.messages.push(message);
      }
    },
    setCrmLeadDetails: (state, action: PayloadAction<LeadDetails>) => {
      state.leadDetails = action.payload;
    },
    clearCrmLeadDetails: (state) => {
      state.leadDetails = null;
    },
    setCrmDefaultChannel: (state, action: PayloadAction<string>) => {
      state.defaultChannel = action.payload;
    },
    setCrmSources: (
      state,
      action: PayloadAction<{
        faqSources: FaqSource[];
        pmsSources: PmsSourceData | null;
      }>,
    ) => {
      state.faqSources = action.payload.faqSources;
      state.pmsSources = action.payload.pmsSources;
    },
    setCrmSourceMessage: (state, action: PayloadAction<string>) => {
      state.sourceMessage = action.payload;
    },
    setCrmSourceLoading: (state, action: PayloadAction<boolean>) => {
      state.sourceLoading = action.payload;
    },
    setCrmCustomQaDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.customQaDrawerOpen = action.payload;
    },
    updateCrmSources: (
      state,
      action: PayloadAction<{
        id: string;
        data: Partial<FaqSource>;
      }>,
    ) => {
      const { id, data } = action.payload;
      const index = state.faqSources.findIndex((source) => source.id === id);
      if (index !== -1) {
        state.faqSources[index] = {
          ...state.faqSources[index],
          ...data,
        };
      }
    },
    clearCrmSourceMessage: (state) => {
      state.sourceMessage = "No sources available";
    },
    clearCrmSources: (state) => {
      state.faqSources = [];
      state.pmsSources = null;
    },
  },
});

export const {
  setCrmMessages,
  clearCrmMessages,
  appendCrmMessage,
  replaceCrmMessage,
  setCrmLeadDetails,
  clearCrmLeadDetails,
  setCrmDefaultChannel,
  setCrmSources,
  setCrmSourceMessage,
  setCrmSourceLoading,
  setCrmCustomQaDrawerOpen,
  updateCrmSources,
  clearCrmSourceMessage,
  clearCrmSources,
} = crmMessageSlice.actions;

export default crmMessageSlice.reducer;
